'use client';

import {
  Process,
  ProcessColumn,
  ProcessDescription,
  ProcessHeader,
  ProcessHeading,
} from '@/components/process';
import { processConfig } from '@/features/process/process.config';
import { useT } from 'next-i18next/client';
import Image from 'next/image';
import React, { type FC } from 'react';

type ProcessStep = 'discovery' | 'design' | 'engineering' | 'launch';

type ProcessItem = {
  id: number;
  key: ProcessStep;
  imageSrc: string;
};

type ProcessType = ProcessItem & {
  heading: string;
  description: string;
  imageAlt: string;
};

type ProcessItemProps = {
  readonly process: ProcessType;
  readonly children?: never;
};

const PROCESS_ITEMS: ProcessItem[] = [
  { id: 1, key: 'discovery', imageSrc: '/images/discovery.avif' },
  { id: 2, key: 'design', imageSrc: '/images/design.avif' },
  { id: 3, key: 'engineering', imageSrc: '/images/engineering.avif' },
  { id: 4, key: 'launch', imageSrc: '/images/launch.avif' },
];

const ProcessItem: FC<ProcessItemProps> = ({ process }) => {
  return (
    <Process>
      <ProcessColumn>
        <ProcessHeading>{process.heading}</ProcessHeading>
        <ProcessDescription>{process.description}</ProcessDescription>
      </ProcessColumn>
      <Image
        src={process.imageSrc}
        className={`ml-0 w-full max-w-[300px] rounded-2xl sm:w-[57rem] sm:max-w-md md:-ml-4 md:max-w-3xl lg:-ml-0`}
        alt={process.imageAlt}
        loading={'eager'}
        width={785}
        height={814}
      />
    </Process>
  );
};

export const ProcessBlock: FC = () => {
  const { t } = useT(processConfig.i18nNamespaces);

  const processData: ProcessType[] = PROCESS_ITEMS.map(
    ({ id, key, imageSrc }) => ({
      id,
      key,
      imageSrc,
      heading: t(`process:${key}.heading`),
      description: t(`process:${key}.description`),
      imageAlt: t(`process:${key}.imageAlt`),
    })
  );

  return (
    <ProcessHeader>
      {processData.map((process) => (
        <ProcessItem key={process.id} process={process} />
      ))}
    </ProcessHeader>
  );
};
